import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Label,
  Modal,
  Pagination,
  Popup,
  Select,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const SIGN_STATUS_OPTIONS = [
  { key: 'all', text: '全部状态', value: '' },
  { key: '0', text: '未签', value: '0' },
  { key: '1', text: '部分已签', value: '1' },
  { key: '2', text: '已完成', value: '2' },
  { key: '3', text: '有拒签', value: '3' },
];

// 第三方 signStatus 语义
const SIGNER_STATUS_LABELS = {
  0: { text: '不可签署', color: 'grey' },
  1: { text: '待签署', color: 'blue' },
  2: { text: '已签署', color: 'green' },
  3: { text: '拒签', color: 'red' },
  4: { text: '可编辑', color: 'teal' },
  9: { text: '顺序未到', color: 'grey' },
};

export const SIGN_STATUS_LABELS = {
  0: { text: '未签', color: 'grey' },
  1: { text: '部分已签', color: 'blue' },
  2: { text: '已完成', color: 'green' },
  3: { text: '有拒签', color: 'red' },
};

export const CALLBACK_STATUS_LABELS = {
  0: { text: '待回调', color: 'orange' },
  1: { text: '已通知', color: 'green' },
  2: { text: '通知失败', color: 'red' },
};

const signerStatusLabel = (status) =>
  SIGNER_STATUS_LABELS[status] || { text: `未知(${status})`, color: 'grey' };

const userTypeText = (userType) => (userType === 2 ? '企业' : '个人');

const ContractTasksTable = () => {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [signStatus, setSignStatus] = useState('');

  const [detail, setDetail] = useState({ open: false, loading: false, data: null });
  const [confirmInfo, setConfirmInfo] = useState({
    open: false,
    title: '',
    content: '',
    danger: false,
    onConfirm: null,
  });
  const [acting, setActing] = useState(false);

  const loadTasks = async (page = 1, kw = keyword, status = signStatus) => {
    setLoading(true);
    let url = `/api/contract/task/?p=${page - 1}`;
    if (kw) {
      url += `&keyword=${encodeURIComponent(kw)}`;
    }
    if (status !== '') {
      url += `&sign_status=${status}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setTasks(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTasks(1).catch((reason) => showError(reason));
  }, []);

  const search = () => {
    loadTasks(1).catch((reason) => showError(reason));
  };

  const openDetail = async (task) => {
    setDetail({ open: true, loading: true, data: null });
    const res = await API.get(`/api/contract/task/${task.id}`);
    const { success, message, data } = res.data;
    if (success) {
      setDetail({ open: true, loading: false, data });
    } else {
      showError(message);
      setDetail({ open: false, loading: false, data: null });
    }
  };

  const syncTask = async (task) => {
    const res = await API.post(`/api/contract/task/${task.id}/sync`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '同步完成');
      await loadTasks(activePage);
      if (detail.open && detail.data) {
        await openDetail(task);
      }
    } else {
      showError(message);
    }
  };

  const retryNotify = async (log) => {
    const res = await API.post(`/api/contract/notify/${log.id}/retry`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '已触发重试');
      if (detail.data) {
        await openDetail({ id: detail.data.id });
      }
    } else {
      showError(message);
    }
  };

  const askConfirm = (title, content, onConfirm, danger = true) => {
    setConfirmInfo({ open: true, title, content, danger, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const runConfirm = async () => {
    const handler = confirmInfo.onConfirm;
    setConfirmInfo((prev) => ({ ...prev, open: false, onConfirm: null }));
    if (handler) {
      setActing(true);
      await handler();
      setActing(false);
    }
  };

  // 任务操作：撤回 / 催签 / 解约 / 单独静默签
  const runTaskOp = async (taskId, op, extra = {}) => {
    const res = await API.post(`/api/contract/task/${taskId}/${op}`, extra);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '操作成功');
      await loadTasks(activePage);
      if (detail.open) {
        await openDetail({ id: taskId });
      }
    } else {
      showError(message);
    }
  };

  const revokeTask = (data) => {
    askConfirm(
      '撤回签署任务',
      `确定撤回任务 ${data.task_no || data.app_task_id} 吗？撤回后签署任务置为已撤销，且无法重新发起。`,
      () => runTaskOp(data.id, 'revoke')
    );
  };

  const urgeTask = (data) => {
    askConfirm(
      '催签',
      '确定对该任务当前待签署节点发送催签短信吗？（电子签平台限制 10 分钟一次）',
      () => runTaskOp(data.id, 'urge'),
      false
    );
  };

  const rescindTask = (data) => {
    askConfirm(
      '解约',
      `确定解约任务 ${data.task_no || data.app_task_id} 吗？解约后任务不可重新发起，且解约任务无法再次解约。`,
      () => runTaskOp(data.id, 'rescind', { reason: '双方协商一致解除原合同' })
    );
  };

  const silentSign = (data, signer) => {
    askConfirm(
      '静默签',
      `确定对 ${signer.name || signer.signer_union_id}（${signer.signer_uid || signer.signer_union_id}）执行静默签署吗？需该主体已完成静默授权。`,
      () => runTaskOp(data.id, 'silent_sign', { union_id: signer.signer_union_id }),
      false
    );
  };

  const detailSigners = detail.data?.signers || [];
  const detailLogs = detail.data?.notify_logs || [];

  return (
    <>
      <Form onSubmit={search}>
        <Form.Group>
          <Form.Input
            icon='search'
            iconPosition='left'
            placeholder='搜索任务编号 / 业务单号 / 标题 / 令牌'
            value={keyword}
            onChange={(e, { value }) => setKeyword(value)}
            width={8}
          />
          <Form.Field width={4}>
            <Select
              options={SIGN_STATUS_OPTIONS}
              value={signStatus}
              onChange={(e, { value }) => setSignStatus(value)}
            />
          </Form.Field>
          <Form.Button loading={loading}>查询</Form.Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>任务编号</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>业务单号</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>标题</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>签署方</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>签署进度</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>任务状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>回调</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>创建时间</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {tasks.map((task) => {
            const signLabel =
              SIGN_STATUS_LABELS[task.sign_status] || { text: task.sign_status, color: 'grey' };
            const cbLabel =
              CALLBACK_STATUS_LABELS[task.callback_status] || { text: '-', color: 'grey' };
            return (
              <Table.Row key={task.id}>
                <Table.Cell>{task.id}</Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{task.task_no || '-'}</code>
                </Table.Cell>
                <Table.Cell>
                  <code style={{ fontSize: '12px' }}>{task.app_task_id}</code>
                </Table.Cell>
                <Table.Cell>
                  <div style={{ fontWeight: 500 }}>{task.task_title || '-'}</div>
                  <div style={{ color: '#8a919f', fontSize: '12px' }}>
                    {task.token_name || '-'}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {task.signed_count}/{task.total_signers}
                </Table.Cell>
                <Table.Cell>
                  <Label basic color={signLabel.color}>
                    {signLabel.text}
                  </Label>
                </Table.Cell>
                <Table.Cell>{task.status_label || task.status}</Table.Cell>
                <Table.Cell>
                  <Label basic color={cbLabel.color}>
                    {cbLabel.text}
                  </Label>
                  {task.callback_attempts > 0 && (
                    <span style={{ marginLeft: '4px', color: '#8a919f', fontSize: '12px' }}>
                      {task.callback_attempts} 次
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {task.created_time > 0 ? timestamp2string(task.created_time) : '-'}
                </Table.Cell>
                <Table.Cell>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    <Button size='tiny' onClick={() => openDetail(task)}>
                      详情
                    </Button>
                    <Button
                      size='tiny'
                      basic
                      disabled={!task.has_local_file}
                      onClick={() => window.open(`/api/contract/task/${task.id}/file`, '_blank')}
                    >
                      原件
                    </Button>
                    {task.preview_url && (
                      <Button
                        size='tiny'
                        basic
                        onClick={() => window.open(task.preview_url, '_blank')}
                      >
                        预览
                      </Button>
                    )}
                    <Button size='tiny' basic onClick={() => syncTask(task)}>
                      同步
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='10'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadTasks(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>共 {total} 条合同任务</span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal
        open={detail.open}
        onClose={() => setDetail({ open: false, loading: false, data: null })}
        size='large'
      >
        <Modal.Header>
          合同任务详情
          {detail.data ? `（${detail.data.task_no || detail.data.app_task_id}）` : ''}
        </Modal.Header>
        <Modal.Content scrolling>
          {detail.loading && <p>加载中...</p>}
          {detail.data && (
            <>
              <Table definition compact size='small'>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>业务单号</Table.Cell>
                    <Table.Cell>{detail.data.app_task_id}</Table.Cell>
                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>任务标题</Table.Cell>
                    <Table.Cell>{detail.data.task_title || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>任务状态</Table.Cell>
                    <Table.Cell>{detail.data.status_label}</Table.Cell>
                    <Table.Cell>签署进度</Table.Cell>
                    <Table.Cell>
                      {detail.data.signed_count}/{detail.data.total_signers}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>发起方 unionId</Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: '12px' }}>
                        {detail.data.initiator_union_id || '-'}
                      </code>
                    </Table.Cell>
                    <Table.Cell>回调地址</Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: '12px' }}>{detail.data.callback_url || '-'}</code>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>原件</Table.Cell>
                    <Table.Cell>{detail.data.file_name || '-'}</Table.Cell>
                    <Table.Cell>签署文件预览</Table.Cell>
                    <Table.Cell>
                      {detail.data.preview_url ? (
                        <a href={detail.data.preview_url} target='_blank' rel='noreferrer'>
                          打开预览
                        </a>
                      ) : (
                        '-'
                      )}
                    </Table.Cell>
                  </Table.Row>
                  {detail.data.refused_reason && (
                    <Table.Row>
                      <Table.Cell>拒签原因</Table.Cell>
                      <Table.Cell colSpan='3'>{detail.data.refused_reason}</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>

              <h4>签署方（{detailSigners.length}）</h4>
              <Table compact size='small'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>签署方 uid / unionId</Table.HeaderCell>
                    <Table.HeaderCell>类型</Table.HeaderCell>
                    <Table.HeaderCell>名称</Table.HeaderCell>
                    <Table.HeaderCell>顺序</Table.HeaderCell>
                    <Table.HeaderCell>签署方式</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>签署时间</Table.HeaderCell>
                    <Table.HeaderCell>签署链接</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {detailSigners.map((signer) => {
                    const label = signerStatusLabel(signer.sign_status);
                    return (
                      <Table.Row key={signer.id}>
                        <Table.Cell>
                          <code style={{ fontSize: '12px' }}>
                            {signer.signer_union_id}
                          </code>
                          {signer.signer_uid ? (
                            <div style={{ color: '#8a919f', fontSize: '12px' }}>
                              uid: {signer.signer_uid}
                            </div>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell>{userTypeText(signer.user_type)}</Table.Cell>
                        <Table.Cell>{signer.name || '-'}</Table.Cell>
                        <Table.Cell>{signer.sort_no || '-'}</Table.Cell>
                        <Table.Cell>
                          {signer.sign_type === 1 ? (
                            <Label basic color='teal'>
                              静默签{signer.silent_signed ? '（已执行）' : ''}
                            </Label>
                          ) : (
                            <Label basic>手动签</Label>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Label basic color={label.color}>
                            {label.text}
                          </Label>
                        </Table.Cell>
                        <Table.Cell>{signer.sign_time || '-'}</Table.Cell>
                        <Table.Cell>
                          {signer.sign_pc_url || signer.sign_h5_url ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {signer.sign_pc_url && (
                                <Button
                                  size='mini'
                                  basic
                                  onClick={() => window.open(signer.sign_pc_url, '_blank')}
                                >
                                  PC
                                </Button>
                              )}
                              {signer.sign_h5_url && (
                                <Button
                                  size='mini'
                                  basic
                                  onClick={() => window.open(signer.sign_h5_url, '_blank')}
                                >
                                  H5
                                </Button>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {signer.sign_status === 2 ? (
                            '-'
                          ) : (
                            <Button
                              size='mini'
                              basic
                              onClick={() => silentSign(detail.data, signer)}
                            >
                              静默签
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>

              <h4>回调记录（最近 50 条）</h4>
              <Table compact size='small'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>方向</Table.HeaderCell>
                    <Table.HeaderCell>事件</Table.HeaderCell>
                    <Table.HeaderCell>签署方</Table.HeaderCell>
                    <Table.HeaderCell>目标地址</Table.HeaderCell>
                    <Table.HeaderCell>HTTP</Table.HeaderCell>
                    <Table.HeaderCell>尝试</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>内容</Table.HeaderCell>
                    <Table.HeaderCell>时间</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {detailLogs.map((log) => (
                    <Table.Row key={log.id}>
                      <Table.Cell>{log.direction === 'out' ? '出站' : '入站'}</Table.Cell>
                      <Table.Cell>{log.event}</Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: '12px' }}>{log.signer_union_id || '-'}</code>
                      </Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: '12px' }}>{log.target_url || '-'}</code>
                      </Table.Cell>
                      <Table.Cell>{log.http_status || '-'}</Table.Cell>
                      <Table.Cell>{log.attempt}</Table.Cell>
                      <Table.Cell>
                        {log.status === 1 ? (
                          <Label basic color='green'>
                            成功
                          </Label>
                        ) : log.status === 2 ? (
                          <Label basic color='red'>
                            失败
                          </Label>
                        ) : (
                          <Label basic color='orange'>
                            待重试
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Popup
                          wide
                          hoverable
                          trigger={<span style={{ fontSize: '12px' }}>查看</span>}
                          content={
                            <pre style={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>
                              {log.raw_body || '-'}
                              {'\n\n响应：'}
                              {log.response_body || '-'}
                              {log.last_error ? `\n\n错误：${log.last_error}` : ''}
                            </pre>
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>
                        {log.created_time > 0 ? timestamp2string(log.created_time) : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        {log.direction === 'out' && log.status !== 1 ? (
                          <Button size='mini' basic onClick={() => retryNotify(log)}>
                            重试
                          </Button>
                        ) : (
                          '-'
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          {detail.data && detail.data.rescinded !== true && (
            <>
              {[0, 5, 10].includes(detail.data.status) && (
                <>
                  <Button basic loading={acting} onClick={() => revokeTask(detail.data)}>
                    撤回任务
                  </Button>
                  <Button basic loading={acting} onClick={() => urgeTask(detail.data)}>
                    催签
                  </Button>
                </>
              )}
              {detail.data.status === 20 && (
                <Button basic color='orange' loading={acting} onClick={() => rescindTask(detail.data)}>
                  解约
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setDetail({ open: false, loading: false, data: null })}>
            关闭
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={confirmInfo.open}
        onClose={closeConfirm}
        size='tiny'
        style={{ maxWidth: '480px' }}
      >
        <Modal.Header>{confirmInfo.title}</Modal.Header>
        <Modal.Content>
          <p>{confirmInfo.content}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeConfirm}>取消</Button>
          <Button color={confirmInfo.danger ? 'red' : 'blue'} onClick={runConfirm}>
            确定
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ContractTasksTable;
